import { __decorate } from "tslib";
// Core
import { Component, Prop, Mixins } from 'vue-property-decorator';
import { required, requiredIf } from 'vuelidate/lib/validators';
// Extends
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import BundleMixin from '@/helpers/vue_mixins/v2/BundleMixin';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
import FormMixin from '@/helpers/vue_mixins/FormMixin';
import FormValidationsMixin from '@/helpers/vue_mixins/v2/FormValidationsMixin';
import PromptUpdaterMixin from '@/helpers/vue_mixins/v3/PromptUpdaterMixin';
import RouterParamsAppMixin from '@/helpers/vue_mixins/RouterParamsAppMixin';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
// Children
import ActionButton from '@/components/action_button/Index.vue';
import AffiliatePublicationDialog from '@/components/v2/AffiliatePublicationDialog.vue';
import AffiliateProductDialog from '@/components/v2/AffiliateProductDialog.vue';
import BaseCloseBtn from '@/components/v2/BaseCloseBtn';
import BaseSubtitle from '@/components/v2/BaseSubtitle';
import BaseTextField from '@/components/v2/BaseTextField.vue';
import BasicTransition from '@/components/page_transition/Index.vue';
import ButtonLoaderMixin from '@/helpers/vue_mixins/ButtonLoaderMixin';
import FacebookPostThumb from '@/components/v3/FacebookPostThumb.vue';
import Modal from '@/components/v3/Modal.vue';
import NumericInput from '@/components/v2/NumericInput.vue';
import PageTitle from '@/components/v2/PageTitle.vue';
import ProductThumb from '@/components/v3/ProductThumb.vue';
// Models / Helpers
import { AFFILIATE } from '@/helpers/constants/CustomBundleKeys';
// Queries / Mutations
import CreateAutomation from '@/plugins/graphql/mutations/v2/your_store/create-automation.gql';
import UpdateAutomation from '@/plugins/graphql/mutations/v2/your_store/update-automation.gql';
import FetchAutomation from '@/plugins/graphql/queries/v2/affiliate/fetch-automation.gql';
import PromptGenerateAutoreply from '@/plugins/graphql/mutations/v3/prompts/generate-comment-autoreply.gql';
let AffiliateAutomationCreate = class AffiliateAutomationCreate extends Mixins(Mixins(BundleMixin, ButtonLoaderMixin, AlertMixin, CleverTapEventsMixin), Mixins(RouterParamsAppMixin, ViewportSizeMixin, FormMixin, FormValidationsMixin), PromptUpdaterMixin) {
    constructor() {
        super(...arguments);
        this.togglePublications = false;
        this.toggleProducts = false;
        this.mutationRequestOnFlight = false;
        this.hasGenerated = false;
        this.generationInProgress = false;
        this.selectedPost = null;
        this.selectedProducts = [];
        this.generatedReason = null;
        this.promptUUID = '';
        this.form = {
            name: '',
            postCoverage: 'specific_post',
            triggerWord: 'any_word',
            automationAction: 'product_payload',
            specificTriggerWords: '',
        };
    }
    get bundleType() {
        return this.bundleKey;
    }
    get buttonTitle() {
        if (this.uuid != null) {
            this.fetchAutomation();
            return 'Update';
        }
        return 'Save';
    }
    fetchAutomation() {
        const self = this;
        this.$apollo
            .query({
            query: FetchAutomation,
            variables: {
                automationUuid: this.uuid,
            },
        })
            .then(({ data }) => {
            const { fetchAutomation } = data;
            self.form.name = fetchAutomation.name;
            // self.form.postCoverage = fetchAutomation.postCoverage;
            self.form.triggerWord = fetchAutomation.triggerWord;
            // self.form.automationAction = fetchAutomation.automationAction;
            if (fetchAutomation.triggerWord === 'specific_word') {
                const words = fetchAutomation.specificTriggerWords.split(', ');
                self.form.specificTriggerWords = words;
            }
            if (fetchAutomation.postId) {
                self.selectedPost = {
                    id: fetchAutomation.postId,
                    message: fetchAutomation?.message,
                    full_picture: fetchAutomation?.imageLink,
                    created_time: fetchAutomation.postCreatedAt,
                    permalink_url: fetchAutomation.postUrl,
                };
            }
            if (fetchAutomation.automationAction === 'product_payload') {
                if (self.bundleType === AFFILIATE) {
                    self.selectedProducts = fetchAutomation.affiliateProducts.map((p) => p.productable);
                }
                else {
                    self.selectedProducts = fetchAutomation.ecommerceProducts;
                }
            }
        });
    }
    get slicedTriggerWords() {
        return this.form.specificTriggerWords.slice(0, 14);
    }
    getImage(img) {
        return img?.galleryBlob.originalUrl;
    }
    truncateTitle(name) {
        if (name.length > 25) {
            return `${name.substring(0, 25)}...`;
        }
        return name;
    }
    openPublications() {
        this.togglePublications = true;
    }
    closePublications() {
        this.togglePublications = false;
    }
    openProducts() {
        this.toggleProducts = true;
    }
    closeProducts() {
        this.toggleProducts = false;
    }
    selectPost(post) {
        this.selectedPost = post;
        this.generate();
    }
    selectProducts(products) {
        this.selectedProducts = products;
    }
    removeTriggerWord(word) {
        this.form.specificTriggerWords = this.form.specificTriggerWords.filter((el) => el !== word);
    }
    removeProduct(productToRemove) {
        // Find the index of the productToRemove in selectedProducts array
        const index = this.selectedProducts.findIndex((product) => product.uuid === productToRemove.uuid);
        if (index !== -1) {
            // Remove the product from the selectedProducts array
            this.selectedProducts.splice(index, 1);
        }
    }
    async onSubmit() {
        this.$v.$touch();
        if (!this.$v.$invalid) {
            this.requestOnFlight();
            if (this.hasGenerated) {
                await this.updateAction(this.promptUUID, 'accept');
                this.genAIAction('Comment Auto-Reply', this.$store.state.appName, 'ACCEPT');
            }
            if (this.uuid == null) {
                await this.automationCreate();
            }
            else {
                await this.automationUpdate();
            }
        }
    }
    onCancel() {
        if (this.hasGenerated) {
            this.genAIAction('Comment Auto-Reply', this.$store.state.appName, 'IGNORE');
        }
        this.$router.push({ name: this.automationRouteSelect().name });
    }
    automationRouteSelect() {
        if (this.bundleKey === 'affiliate-bundle') {
            return { name: 'affiliate-automation' };
        }
        return { name: 'ecommerce-affiliate-automation' };
    }
    generate() {
        this.generationInProgress = true;
        if (this.hasGenerated) {
            this.updateAction(this.promptUUID, 'regenerate');
            this.genAIAction('Comment Auto-Reply', this.$store.state.appName, 'REGENERATE');
        }
        else {
            this.genAIAction('Comment Auto-Reply', this.$store.state.appName, 'GENERATE');
        }
        return this.$apollo
            .mutate({
            mutation: PromptGenerateAutoreply,
            variables: {
                input: {
                    appUuid: this.$store.state.appUUID,
                    post: {
                        id: this.selectedPost.id,
                        permalinkUrl: this.selectedPost.permalink_url,
                        message: this.selectedPost.message,
                        fullPicture: this.selectedPost?.full_picture,
                        createdTime: this.selectedPost.created_time,
                    },
                },
            },
        })
            .then((result) => {
            this.hasGenerated = true;
            this.generationInProgress = false;
            this.form.name = result.data.generateCommentAutoreply.prompt.name;
            this.selectedProducts = result.data.generateCommentAutoreply.prompt.products;
            this.generatedReason = result.data.generateCommentAutoreply.prompt.jsonCompletion.reason;
            this.promptUUID = result.data.generateCommentAutoreply.base.uuid;
        })
            .catch((e) => {
            this.generationInProgress = false;
            this.errorNotification(e.message);
        });
    }
    automationUpdate() {
        return this.$apollo
            .mutate({
            mutation: UpdateAutomation,
            variables: {
                input: this.automationCreateInput(),
            },
        })
            .then(() => {
            const route = this.automationRouteSelect();
            this.automationAction('SUBMIT EDIT');
            this.successNotification('Comment Auto-Reply successfully updated.');
            this.$router.push({ name: route.name });
        })
            .catch((error) => {
            this.errorNotification(error.message);
        });
    }
    automationCreate() {
        return this.$apollo
            .mutate({
            mutation: CreateAutomation,
            variables: {
                input: this.automationCreateInput(),
            },
        })
            .then(() => {
            const route = this.automationRouteSelect();
            this.automationAction('SUBMIT CREATE');
            this.successNotification('Comment Auto-Reply successfully created.');
            this.$router.push({ name: route.name });
        })
            .catch((error) => {
            this.errorNotification(error.message);
        });
    }
    automationCreateInput() {
        const { form } = this;
        const appUuid = this.appUUID;
        let input = { appUuid, ...form };
        input.fromAffiliate = this.bundleKey === AFFILIATE;
        if (form.specificTriggerWords.length > 0) {
            input.specificTriggerWords = form.specificTriggerWords.join(', ');
            input.triggerWord = 'specific_word';
        }
        else {
            input.specificTriggerWords = '';
            input.triggerWord = 'any_word';
        }
        const post = this.selectedPost;
        input = {
            ...input,
            imageLink: post?.full_picture,
            postUrl: post?.permalink_url,
            postCreatedAt: post?.created_time,
            message: post?.message,
            postId: post?.id,
        };
        // TODO: Products are always required?
        // if (this.selectedProducts) {
        input.productUuids = this.selectedProducts?.map((p) => p.uuid);
        // }
        if (this.uuid !== null) {
            input.automationUuid = this.uuid;
        }
        return input;
    }
};
__decorate([
    Prop({ type: String })
], AffiliateAutomationCreate.prototype, "uuid", void 0);
AffiliateAutomationCreate = __decorate([
    Component({
        components: {
            ActionButton,
            AffiliatePublicationDialog,
            AffiliateProductDialog,
            BaseCloseBtn,
            BaseSubtitle,
            BaseTextField,
            BasicTransition,
            FacebookPostThumb,
            Modal,
            NumericInput,
            PageTitle,
            ProductThumb,
        },
        validations: {
            form: {
                name: { required },
                postCoverage: {
                    required,
                    mustHaveSelectedPost() {
                        const post = this.selectedPost;
                        return !!post;
                    },
                },
                triggerWord: { required },
                specificTriggerWords: {
                    required: requiredIf((form = this.form) => {
                        const triggerWordType = form.triggerWord;
                        return triggerWordType === 'specific_word';
                    }),
                },
                automationAction: {
                    required,
                    mustHaveSelectedProduct() {
                        const products = this.selectedProducts;
                        return products.length > 0;
                    },
                },
            },
        },
    })
], AffiliateAutomationCreate);
export default AffiliateAutomationCreate;
